import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, doc, getDoc, getDocs, onSnapshot, orderBy, query, setDoc } from "firebase/firestore";
import { database } from "../../database/firebase";
import { useSelector, useDispatch } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import "./waiting.css";
import { setBackButtonUrl } from "../uiSlice";
import { createInitialState } from "../shiftTicTacToe/engine/gameLogic";
import {
  nextRematchSeqForLobbyStart,
  startingPlayerForRematchSeq,
} from "../game/alternateStartStorage";
import {
  characterImageSrc,
  fetchCharacterImagesByUserIds,
  parseHudCharacter,
} from "../game/useLobbyPvpFirestore";

const TOTAL_USER_COUNT_KEY = "mythFactTotalUserCount";
const CURRENT_LOBBY_KEY = "mythFactCurrentLobby";
const MAX_LOBBY_PLAYERS = 2;
const CHARACTER_SLOTS = ["c1", "c2"];

function PersonIcon() {
  return (
    <span className="waiting-person-icon-wrap" aria-hidden="true">
      <svg
        className="waiting-person-icon"
        viewBox="0 0 64 72"
        preserveAspectRatio="xMidYMid meet"
      >
        <circle cx="32" cy="14" r="9" fill="none" stroke="currentColor" strokeWidth="2.25" />
        <path
          d="M10 68 C10 46 20 38 32 38 C44 38 54 46 54 68"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.25"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

function sortLiveUsersForCharacterAssign(users) {
  return [...users].sort((a, b) => {
    const ta = Number(a?.timestamp ?? 0);
    const tb = Number(b?.timestamp ?? 0);
    if (ta !== tb) return ta - tb;
    return String(a?.userId ?? a?.id ?? "").localeCompare(
      String(b?.userId ?? b?.id ?? "")
    );
  });
}

function pickAvailableCharacter(users, fallbackIndex = 0) {
  const used = new Set(
    users
      .map((u) => normalizeCharacterName(u?.character))
      .filter(Boolean)
  );
  const next = CHARACTER_SLOTS.find((slot) => !used.has(slot));
  return next || CHARACTER_SLOTS[Math.min(fallbackIndex, CHARACTER_SLOTS.length - 1)];
}

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function resolveLobbyName(locationState, userData) {
  const fromState =
    typeof locationState?.lobbyName === "string" ? locationState.lobbyName.trim() : "";
  if (fromState) return fromState;
  try {
    const fromSession = sessionStorage.getItem(CURRENT_LOBBY_KEY);
    if (fromSession && fromSession.trim()) return fromSession.trim();
  } catch {
    // ignore storage failures
  }
  return String(userData?.lobby ?? "").trim();
}

function stripNullWords(value) {
  return String(value ?? "")
    .replace(/\bnull\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

function getDisplayName(userData) {
  const name = stripNullWords(userData?.name);
  if (name) return name;
  const first = stripNullWords(userData?.firstName);
  if (first) return first;
  const last = stripNullWords(userData?.lastName);
  if (last) return last;
  return `${first} ${last}`.trim();
}

function normalizeCharacterName(character) {
  const value = String(character || "").trim().toLowerCase();
  return /^c[12]$/.test(value) ? value : "";
}

function isPresenterValue(value) {
  if (value === true) return true;
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "true" || normalized === "1" || normalized === "yes";
}

function getPlayerLabel(player, fallbackUserData, order) {
  const fromName = stripNullWords(player?.name);
  if (fromName) return fromName;

  const first = stripNullWords(player?.firstName ?? player?.first_name);
  const last = stripNullWords(player?.lastName ?? player?.last_name);
  const fullName = `${first} ${last}`.trim();
  if (fullName) return fullName;

  const fromEmail = String(player?.email ?? "").trim();
  if (fromEmail) return fromEmail;

  const fromEmpId = String(player?.emp_id ?? player?.employeeId ?? "").trim();
  if (fromEmpId) return fromEmpId;

  const fallbackName = stripNullWords(fallbackUserData?.name);
  if (fallbackName) return fallbackName;

  const fallbackEmail = String(fallbackUserData?.email ?? "").trim();
  if (fallbackEmail) return fallbackEmail;

  const fallbackEmpId = String(
    fallbackUserData?.emp_id ?? fallbackUserData?.employeeId ?? ""
  ).trim();
  if (fallbackEmpId) return fallbackEmpId;

  return `Player ${order}`;
}

export default function Waiting() {
  const navigate = useNavigate();
  const location = useLocation();
  const adminToken = useSelector(selectAdminToken);
  const themeData = useSelector((state) => state.theme?.data);
  const themeColors = themeData?.colors || themeData || {};
  const buttonStyle =
    themeColors?.option_color != null && themeColors?.option_text_color != null
      ? {
          backgroundColor: themeColors.option_color,
          color: themeColors.option_text_color,
        }
      : undefined;
  const themeCurrentName = useSelector((state) => state.theme?.currentTheme);
  const userData = useMemo(() => readStoredUserData(), []);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const [isPresenter, setIsPresenter] = useState(() => isPresenterValue(userData?.presenter));
  const [questionsInitialized, setQuestionsInitialized] = useState(false);
  const [characterImageByUserId, setCharacterImageByUserId] = useState({});

  const userId = String(userData?.userId ?? userData?.userid ?? userData?.id ?? "").trim();
  const sessionId = String(userData?.sessionId ?? "").trim();
  const lobbyName = useMemo(
    () => resolveLobbyName(location.state, userData),
    [location.state, userData]
  );
  const displayName = getDisplayName(userData);
  const { status, user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  useEffect(() => {
    document.documentElement.classList.add("waiting-page-bg");
    document.body.classList.add("waiting-page-bg");
    try {
      sessionStorage.setItem(TOTAL_USER_COUNT_KEY, String(MAX_LOBBY_PLAYERS));
    } catch {
      // ignore storage failures
    }
    return () => {
      document.documentElement.classList.remove("waiting-page-bg");
      document.body.classList.remove("waiting-page-bg");
    };
  }, []);

  useEffect(() => {
    dispatch(setBackButtonUrl("lobby"));
  }, [dispatch, status, user]);

  useEffect(() => {
    if (!lobbyName) return;
    try {
      sessionStorage.setItem(CURRENT_LOBBY_KEY, lobbyName);
    } catch {
      // ignore storage failures
    }
    if (String(userData?.lobby ?? "").trim() === lobbyName) return;
    try {
      const merged = { ...(userData || {}), lobby: lobbyName };
      const payload = JSON.stringify(merged);
      localStorage.setItem("userData", payload);
      sessionStorage.setItem("userData", payload);
    } catch {
      // ignore storage failures
    }
  }, [lobbyName, userData]);

  useEffect(() => {
    if (!userId || !sessionId || !lobbyName) return;

    const organizationId = String(userData?.organizationId ?? "").trim();
    const themeName = String(themeData?.themename ?? themeCurrentName ?? "").trim();
    if (!adminToken || !organizationId || !themeName) return;

  
  }, [
    adminToken,
    displayName,
    lobbyName,
    sessionId,
    themeCurrentName,
    themeData?.themename,
    userData?.email,
    userData?.organizationId,
    userId,
  ]);

  const handlePlayNow = async () => {
    if (starting) return;
    setError("");

    if (!isPresenter) {
      setError("Only presenter can start the game.");
      return;
    }

    if (players.length !== MAX_LOBBY_PLAYERS) {
      setError("Waiting for opponent to join...");
      return;
    }

    if (!userId || !sessionId || !lobbyName) {
      setError("Missing lobby details. Please join the lobby again.");
      return;
    }

    try {
      setStarting(true);

      const gameDocRef = doc(database, `lobbies/${sessionId}/lobby/${lobbyName}`);
      const existing = await getDoc(gameDocRef);
      const existingStatus = String(existing.data()?.status ?? "").trim().toLowerCase();

      if (existing.exists() && existingStatus === "play") {
        try {
          sessionStorage.setItem("gameMode", "pvp");
        } catch {
          // ignore
        }
        navigate("/game");
        return;
      }
      if (existing.exists() && existingStatus === "gameover") {
        navigate("/leaderboard");
        return;
      }

      const existingData = existing.data() || {};
      const nextRematch = nextRematchSeqForLobbyStart(existingData);
      const startingPlayer = startingPlayerForRematchSeq(nextRematch);
      const shiftGameState = createInitialState({
        maxPieces: 4,
        allowDiagonal: true,
        startingPlayer,
      });

      const playPayload = {
        status: "play",
        time: "30",
        turn: "",
        totalUserCount: MAX_LOBBY_PLAYERS,
        questions: themeData?.questions || [],
        currentQuestion: 0,
        shiftGameState,
        shiftGameRevision: 0,
        shiftGameUpdatedAt: Date.now(),
        shiftGameUpdatedBy: userId,
        shiftGameRematchSeq: nextRematch,
        shiftGameScoreSig: "",
      };

      await setDoc(
        gameDocRef,
        existing.exists()
          ? playPayload
          : {
              ...playPayload,
              createdAt: Date.now(),
              createdBy: userId,
            },
        { merge: true }
      );

      try {
        sessionStorage.setItem("gameMode", "pvp");
      } catch {
        // ignore
      }
      navigate("/game");
    } catch (e) {
      console.error("Play Now failed:", e);
      setError(String(e?.message || e || "Failed to start game."));
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    if (!userId || !sessionId || !lobbyName) {
      setError("Missing lobby details. Please join the lobby again.");
      setLoading(false);
      return;
    }

    let unsubscribeUsers = null;
    let unsubscribeGame = null;
    let isActive = true;
    const usersPath = `lobbies/${sessionId}/lobby/${lobbyName}/users`;
    const usersRef = collection(database, usersPath);
    const userDocRef = doc(database, `${usersPath}/${userId}`);
    const gameDocRef = doc(database, `lobbies/${sessionId}/lobby/${lobbyName}`);

    const joinCurrentUser = async () => {
      try {
        if (!isActive) return;
        const existingGameDoc = await getDoc(gameDocRef);
        const existingGameStatus = String(
          existingGameDoc.data()?.status ?? ""
        ).trim().toLowerCase();
        if (existingGameStatus === "gameover") {
          navigate("/leaderboard");
          return;
        }
        const existingUserDoc = await getDoc(userDocRef);
        if (!existingUserDoc.exists()) {
          const presenter = isPresenterValue(userData?.presenter);
          const usersSnapshot = await getDocs(usersRef);
          const liveUsers = usersSnapshot.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((p) => p?.status === "live");
          const assignedCharacter = pickAvailableCharacter(liveUsers, liveUsers.length);

          await setDoc(
            userDocRef,
            {
              userId,
              sessionId,
              lobby: lobbyName,
              organizationId: userData?.organizationId || "",
              name: displayName,
              email: userData?.email || "",
              emp_id: userData?.emp_id || userData?.employeeId || "",
              employeeId: userData?.employeeId || userData?.emp_id || "",
              timestamp: Date.now(),
              status: "live",
              presenter,
              character: assignedCharacter,
            },
            { merge: true }
          );
          if (isActive) setIsPresenter(presenter);
        } else {
          const existingPresenter = isPresenterValue(existingUserDoc.data()?.presenter);
          const existingCharacter = normalizeCharacterName(existingUserDoc.data()?.character);
          let patch = { status: "live" };

          if (!existingCharacter) {
            const usersSnapshot = await getDocs(usersRef);
            const liveUsers = usersSnapshot.docs
              .map((d) => ({ id: d.id, ...d.data() }))
              .filter((p) => String(p?.userId || "").trim() !== userId && p?.status === "live");
            patch = {
              ...patch,
              character: pickAvailableCharacter(liveUsers, liveUsers.length),
            };
          }

          await setDoc(userDocRef, patch, { merge: true });
          if (isActive) setIsPresenter(existingPresenter);
        }
      } catch (e) {
        if (isActive) {
          console.error("Failed to upsert current lobby user:", e);
        }
      }
    };

    joinCurrentUser();

    unsubscribeUsers = onSnapshot(
      query(usersRef, orderBy("timestamp", "asc")),
      (snap) => {
        if (!isActive) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const sortedLive = sortLiveUsersForCharacterAssign(
          list.filter((p) => p?.status === "live")
        );
        setPlayers(sortedLive.slice(0, MAX_LOBBY_PLAYERS));
        setLoading(false);
      },
      (e) => {
        if (!isActive) return;
        console.error("Waiting lobby snapshot failed:", e);
        setError("Could not load lobby players.");
        setLoading(false);
      }
    );

    unsubscribeGame = onSnapshot(
      gameDocRef,
      (snap) => {
        if (!isActive || !snap.exists()) return;
        const gameStatus = String(snap.data()?.status ?? "").trim().toLowerCase();
        if (gameStatus === "play") {
          try {
            sessionStorage.setItem("gameMode", "pvp");
          } catch {
            // ignore
          }
          navigate("/game");
          return;
        }
        if (gameStatus === "gameover") {
          navigate("/leaderboard");
        }
      },
      (e) => {
        if (!isActive) return;
        console.error("Waiting game status snapshot failed:", e);
      }
    );

    return () => {
      isActive = false;
      if (unsubscribeUsers) unsubscribeUsers();
      if (unsubscribeGame) unsubscribeGame();
    };
  }, [displayName, lobbyName, navigate, sessionId, userData?.email, userData?.organizationId, userId]);

  useEffect(() => {
    if (!sessionId || !lobbyName) {
      setCharacterImageByUserId({});
      return undefined;
    }

    const ids = players
      .map((p) => String(p?.userId ?? p?.id ?? "").trim())
      .filter(Boolean);

    if (!ids.length) {
      setCharacterImageByUserId({});
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const byUserId = await fetchCharacterImagesByUserIds(
          sessionId,
          lobbyName,
          ids
        );

        const withLobbyFallback = { ...byUserId };
        players.forEach((p) => {
          const uid = String(p?.userId ?? p?.id ?? "").trim();
          if (!uid || withLobbyFallback[uid]) return;
          const src = characterImageSrc(parseHudCharacter(p?.character));
          if (src) withLobbyFallback[uid] = src;
        });

        if (!cancelled) setCharacterImageByUserId(withLobbyFallback);
      } catch (e) {
        console.warn("Waiting room character fetch failed:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, lobbyName, players]);

  const slots = useMemo(() => {
    const filled = players.map((p, idx) => {
      const uid = String(p?.userId ?? p?.id ?? "").trim();
      return {
        joined: true,
        userId: uid,
        label: getPlayerLabel(p, uid === userId ? userData : null, idx + 1),
        isMe: uid === userId,
        characterImageSrc: uid ? characterImageByUserId[uid] : null,
      };
    });
    while (filled.length < MAX_LOBBY_PLAYERS) {
      filled.push({
        joined: false,
        label: "......",
        isMe: false,
      });
    }
    return filled;
  }, [players, userData, userId, characterImageByUserId]);

  const [leftPlayer, rightPlayer] = slots;

  const renderPlayerSlot = (slot) => (
    <div
      className={`waiting-player ${slot.joined ? "joined" : "empty"} ${slot.isMe ? "me" : ""}`}
    >
      <div className="waiting-card-glass">
        <div className="waiting-avatar">
          {slot.joined ? (
            slot.characterImageSrc ? (
              <img
                src={slot.characterImageSrc}
                alt=""
                className="waiting-character-img"
              />
            ) : (
              <PersonIcon />
            )
          ) : (
            <span className="waiting-placeholder">···</span>
          )}
        </div>
      </div>
      <p className="waiting-player-name">{slot.label}</p>
    </div>
  );

  return (
    <div className="waiting-page">
      <div className="waiting-grid-floor waiting-grid-floor--top" aria-hidden="true" />
      <div className="waiting-grid-floor waiting-grid-floor--bottom" aria-hidden="true" />

      <p className="waiting-status-line">
        {loading ? "Loading lobby..." : "Waiting for players.."}
      </p>

      {error ? <p className="waiting-error">{error}</p> : null}

      <div className="waiting-vs-row">
        {renderPlayerSlot(leftPlayer)}
        <div className="waiting-vs" aria-hidden="true">
          <span className="waiting-vs-glow" />
          <span className="waiting-vs-sparks" />
          <span className="waiting-vs-text">vs</span>
        </div>
        {renderPlayerSlot(rightPlayer)}
      </div>

      {isPresenter ? (
        <button
          className="waiting-play-btn"
          type="button"
          disabled={players.length !== MAX_LOBBY_PLAYERS || starting}
          onClick={handlePlayNow}
          style={buttonStyle}
        >
          {starting ? "Starting..." : "Play Now"}
        </button>
      ) : null}
    </div>
  );
}
